import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import { useLocation, useNavigate } from "react-router-dom";
import defaultArtwork from "../../../../src/images/artwork4.jpg";
import useCoverTint from "../../../hooks/useCoverTint";
import useGetAlbums from "../../../hooks/useGetAlbums";
import { TypeOfWindowUndefined, cap, getOptimizedImageUrl, parseURLParamsFrom, removeHyphens } from "../../_helpers";
import Footer from "../../footer";
import Layout from "../../layout";
import { CinematicSky } from "../../ui/CinematicSky";
import { LazyDottedSurface } from "../../ui/LazyDottedSurface";
import { BackgroundSwitcher } from "../../backgrounds/BackgroundSwitcher";
import { PageBackground } from "../../backgrounds/PageBackground";
import { usePageBackgroundPlan } from "../../backgrounds/usePageBackgroundPlan";
import { classicLookVisible, newestRelease } from "../../../utils/pageBackgrounds";
import { toArtistSlug } from "../../../utils/artistSlug";
import { ArtistNav } from "./ArtistNav";
import { BioSection } from "./BioSection";
import { ContactSection } from "./ContactSection";
import { GallerySection } from "./GallerySection";
import { GamesSection } from "./GamesSection";
import { HeroSection } from "./HeroSection";
import { MerchSection } from "./MerchSection";
import { MusicSection } from "./MusicSection";
import { TourDatesSection } from "./TourDatesSection";
import { VideoSection } from "./VideoSection";

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const AnimatedSection = ({ children, delay = 0 }) => (
  <motion.div
    className="AnimatedSection"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={sectionVariants}
    transition={{ delay }}
    style={{ willChange: "transform, opacity", width: "100%" }}
  >
    {children}
  </motion.div>
);

export const defaultArtistData = {
  artist: "Truevined",
  artistBio: "Singer, Rapper, Producer",
  featuredArtist: null,
  title: "Gold Soul",
  artwork: defaultArtwork,
  audioURL:
    "https://p.scdn.co/mp3-preview/6bb8a2b5cef0fa6d448b8503e4e170ea182c0157?cid=5225c4ca15f44b7aac57df853c6b5020",
  spotifyURL: "https://open.spotify.com/playlist/7vnuuglFyRoSq8rvteszqI",
  appleMusicURL: "https://geo.music.apple.com/ca/album/_/1623875173?i=1623875180",
  youtubeURL: "https://www.youtube.com/watch?v=p5uAOmsaFoM&ab_channel=Truevined",
  facebookPixel: "530282730968435",
  event: "",
};

function ArtistTemplate(props) {
  let artist = props.artist || "";
  let grain = props.showGrain || false;
  const artwork = artist?.artwork || null;
  const navigate = useNavigate();

  const [song, setSong] = useState(null);
  const [artistName, setArtistName] = useState(null);
  const [releaseName, setReleaseName] = useState(null);
  const [track, setTrack] = useState(null);

  if (!artist) {
    console.log("no artist data sad face 🥺");
    return null;
  }

  const goToArtistHomepage = (artistUsername) => {
    if (artistUsername) {
      navigate(`${artistUsername}`);
    }
  };

  const trackData = useGetAlbums(
    {
      artistId: artistName,
    },
    null,
    [artistName]
  );

  useEffect(() => {
    let artistFromUrl = getDataFromURL("artist");
    // Use artist name from URL, or fall back to props artist name
    const resolvedArtistName = artistFromUrl || artist?.artistData?.name || "Truevined";
    setArtistName(resolvedArtistName);

    let song = getDataFromURL("albumName");
    setReleaseName(removeHyphens(song));

    console.log("inside artistTemplate useEffect", resolvedArtistName, song);
  }, [artist?.artistData?.name]);

  const getDataFromURL = (choice) => {
    console.log("inside getSongFromURL");
    if (TypeOfWindowUndefined()) {
      return null;
    }

    // Extract artistname and albumname from the URL in the browser
    const pathname = window.location.pathname;
    const parts = pathname.split("/").filter((part) => part !== ""); // Remove empty parts

    if (choice === "artist") {
      return parts[0];
    }
    if (choice === "albumName") {
      return parts[1];
    }

    return null;
  };

  useEffect(() => {
    if (trackData?.error) {
      console.log("error:", trackData.error);
      return;
    }

    if (trackData) {
      // all albums
      let song = {
        artist: artistName,
        artistUsername: artistName,
        artistBio: "Singer, Rapper, Producer",
        // featuredArtist: null,
        title: `Explore ${cap(artistName)}'s ${trackData.albums.filter((album) => album.album_group === "album").length
          } Albums`, // trackData.name,
        artwork: trackData?.albums?.[0]?.images?.[0]?.url || "", // trackData.images[0].url,
        audioURL: null, //trackData.tracks[0].preview_url,
        spotifyURL: "", //trackData.external_urls.spotify,
        spotifyArtistURL: trackData?.albums?.[0]?.artists?.[0]?.external_urls?.spotify || "",
        // appleMusicURL: "https://geo.music.apple.com/ca/album/_/1634706525?i=1634706736",
        // youtubeURL: "https://www.youtube.com/watch?v=clt-He56mOM",
        // facebookPixel: "530282730968435",
        trackData,
      };
      setSong(song);
      setTrack(trackData.albums);
    }
  }, [trackData]);

  const location = useLocation();
  const adMode = Boolean(parseURLParamsFrom(location.search).adMode);
  const backgroundArtistSlug = toArtistSlug(artist.artistData?.name);
  const newestAlbum = useMemo(() => newestRelease(trackData?.albums), [trackData?.albums]);
  const newestCoverUrl = newestAlbum?.images?.[0]?.url;
  const newestCoverTint = useCoverTint(newestCoverUrl);
  const toneSampleUrl = newestCoverUrl ? getOptimizedImageUrl(newestCoverUrl, { width: 48, height: 48, fit: "cover", quality: 60 }) : null;
  const heroBackground = usePageBackgroundPlan({ kind: "home", artistSlug: backgroundArtistSlug, releaseKey: newestAlbum?.id ?? null, coverUrl: toneSampleUrl });
  const heroBackgroundReplacesClassic = !classicLookVisible(heroBackground);

  console.log("Artist data loaded 🎉", artist);
  console.log("Track data loaded 🎉", trackData);
  console.log("Song data loaded 🎉", song);

  return (
    <Layout song={artist} artwork={artist.artistData?.images?.[0]?.url || ""} showGrain={grain}>
      <BackgroundSwitcher kind="home" artistSlug={backgroundArtistSlug} plan={heroBackground} adMode={adMode} coverUrl={newestCoverUrl} coverTint={newestCoverTint} />
      {/* <SEO title={`${artist.title}${artist.featuredArtist ? ` ft. ${artist.featuredArtist}` : ""}`} /> */}
      <Container className="release-landing-page">
        {/* <Link to={"#" || "/" + artist.artistUsername} className="logo" style={{ transform: `scale(${logoScale})` }}>
          <TruevinedSVGLogo color={"var(--logoColor)"} width={164} alt="Truevined logo" />
        </Link> */}
        {/* <div className="artwork_bg" style={{ backgroundImage: `url(${artwork})` }} /> */}
        <div className="outer-container">
          <div className={`inner-container`}>
            <div
              className="FullColorVersion2022"
              style={{
                width: "100%",
                maxWidth: "100vw",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <div
                className="Background"
                style={{
                  position: "fixed",
                  inset: "0",
                  width: "100%",
                  height: "100%",
                  backgroundColor: "var(--backgroundColor2)",
                }}
              />
              <Box
                className="HeroContainer"
                sx={{
                  position: "relative",
                  minHeight: "100vh",
                  overflow: "visible",
                  width: "100%",
                  isolation: "isolate",
                }}
              >
                {heroBackgroundReplacesClassic ? null : (
                  <>
                    <CinematicSky className="CinematicSky" />
                    <LazyDottedSurface
                      key={`dotted-surface-${artist.artistData.name}`}
                      delay={1500}
                      audioReactive={true}
                      gyroscopeEnabled={true}
                      sx={{
                        zIndex: 1,
                        opacity: { xs: 0.34, md: 0.38 },
                        WebkitMaskImage:
                          "linear-gradient(to bottom, transparent 0%, transparent 42%, rgba(0,0,0,0.46) 62%, #000 80%, #000 100%)",
                        maskImage:
                          "linear-gradient(to bottom, transparent 0%, transparent 42%, rgba(0,0,0,0.46) 62%, #000 80%, #000 100%)",
                      }}
                    />
                  </>
                )}
                <PageBackground
                  plan={heroBackground}
                  kind="home"
                  placement="hero"
                  seedKey="home"
                  delayMs={400}
                />
                <ArtistNav artist={artist.artistData.name} artistImage={artist.artistData?.images?.[0]?.url} />
                <AnimatedSection delay={0}>
                  <HeroSection artistData={artist.artistData} song={song} />
                </AnimatedSection>
              </Box>
              <AnimatedSection delay={0.1}>
                <MusicSection artistData={artist.artistData} song={song} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <GamesSection artistData={artist.artistData} song={song} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <MerchSection artistData={artist.artistData} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <BioSection artistData={artist.artistData} song={song} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <TourDatesSection artistData={artist.artistData} song={song} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <VideoSection artistData={artist.artistData} song={song} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <GallerySection artistData={artist.artistData} />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <ContactSection artistData={artist.artistData} song={song} />
              </AnimatedSection>
              <Footer artistData={artist.artistData} song={artist} surface="artist" />
            </div>
          </div>
        </div>
        {/* <div className="artwork_bg bottom outer">
          <div className="artwork_bg bottom" style={{ backgroundImage: `url(${artwork})` }} />
        </div> */}
      </Container>
    </Layout>
  );
}

export default ArtistTemplate;
